import {
  getSeatAndRowSlices,
  getSeatingRowValue,
  type PdmFlatRow,
} from "./normalizePdm"

export type InferredRowLayout = "2-row" | "3-row"
export type SeatingRowLayout = "1-row" | "2-row" | "3-row" | ""
export type SeatingRowMatch = "yes" | "no" | "skip"

export interface SeatingRowCompareResult {
  inferredLayout: InferredRowLayout
  seatingRowLayout: SeatingRowLayout
  match: SeatingRowMatch
  note: string
}

/**
 * App rule: a middle-row value means 3-row. No middle-row value means 2-row.
 */
export function inferRowLayoutFromMiddleRow(
  middleRow: string
): InferredRowLayout {
  return middleRow.trim() ? "3-row" : "2-row"
}

/**
 * Turn a PDM Seating Row value such as "2 Row" or "3 Row" into a layout label.
 */
export function parseSeatingRowLayout(seatingRow: string): SeatingRowLayout {
  const normalized = seatingRow
    .trim()
    .toLowerCase()
    .replace(/[-_]/g, " ")
    .replace(/\s+/g, " ")
  if (!normalized) return ""
  if (normalized === "1 row" || normalized === "1row") return "1-row"
  if (normalized === "2 row" || normalized === "2row") return "2-row"
  if (normalized === "3 row" || normalized === "3row") return "3-row"
  return ""
}

/**
 * Compare PDM Seating Row with the app's middle-row 2-row/3-row rule.
 * Skips empty, 1-row, and unknown Seating Row values.
 */
export function compareSeatingRowToInferred(
  seatingRow: string,
  middleRow: string
): SeatingRowCompareResult {
  const inferredLayout = inferRowLayoutFromMiddleRow(middleRow)
  const seatingRowLayout = parseSeatingRowLayout(seatingRow)

  if (!seatingRow.trim()) {
    return {
      inferredLayout,
      seatingRowLayout,
      match: "skip",
      note: "Seating Row is empty — cannot compare",
    }
  }

  if (seatingRowLayout === "1-row") {
    return {
      inferredLayout,
      seatingRowLayout,
      match: "skip",
      note: "Seating Row is 1 Row — not used for 2-row/3-row",
    }
  }

  if (seatingRowLayout !== "2-row" && seatingRowLayout !== "3-row") {
    return {
      inferredLayout,
      seatingRowLayout,
      match: "skip",
      note: `Seating Row value "${seatingRow}" is not 2 Row or 3 Row`,
    }
  }

  if (seatingRowLayout === inferredLayout) {
    return {
      inferredLayout,
      seatingRowLayout,
      match: "yes",
      note: "",
    }
  }

  return {
    inferredLayout,
    seatingRowLayout,
    match: "no",
    note: `Mismatch: middle row says ${inferredLayout} but Seating Row says ${seatingRowLayout}`,
  }
}

/**
 * When any PDM product for this vehicle has Seating Row that disagrees with
 * the middle-row rule, return that PDM layout so the Rows step can preselect it.
 * Returns null when every comparable product matches (no picker needed).
 */
export function resolveRowCountChoiceDefault(
  flat: PdmFlatRow[]
): InferredRowLayout | null {
  for (const row of flat) {
    const { middleRow } = getSeatAndRowSlices(row.product)
    const seatingRow = getSeatingRowValue(row.product)
    const result = compareSeatingRowToInferred(seatingRow, middleRow)
    if (
      result.match === "no" &&
      (result.seatingRowLayout === "2-row" ||
        result.seatingRowLayout === "3-row")
    ) {
      return result.seatingRowLayout
    }
  }
  return null
}
